// Identical fetch wrapper to apps/web/src/lib/api.ts — same API, same auth.
// Hub talks to the same Express backend; the only difference is which static
// dist is served based on the Host header (hub.getmerchd.com vs app.getmerchd.com).
import type { RefreshOutcome } from "./session-refresh";

let getAccessToken: () => string | null = () => null;
let refreshToken: () => Promise<RefreshOutcome> = async () => ({ kind: "signed_out" });
let onLogout: () => void = () => {};

export function bindAuth(args: {
  getAccessToken: () => string | null;
  refreshToken: () => Promise<RefreshOutcome>;
  onLogout: () => void;
}): void {
  getAccessToken = args.getAccessToken;
  refreshToken = args.refreshToken;
  onLogout = args.onLogout;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

export interface ApiOptions {
  skipAuthRefresh?: boolean;
  skipAuth?: boolean;
}

/** Human-readable message from a failed api() call — prefers the server's
 *  `{ error }` body over the generic "HTTP <status>". */
export function apiErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: unknown } | null;
    if (body && typeof body.error === "string") return body.error;
    return e.message;
  }
  return e instanceof Error ? e.message : fallback;
}

async function rawFetch(
  input: RequestInfo,
  init: RequestInit,
): Promise<Response> {
  return fetch(input, {
    credentials: "include",
    ...init,
  });
}

/**
 * The access token ran out (401): renew it. Only a definitive "signed out"
 * from the server signs the person out; a deploy or a blip that outlasts the
 * retries throws a 503 and KEEPS the session (stay signed in, 2026-10-22 —
 * lib/session-refresh.ts).
 */
async function renewedToken(): Promise<string> {
  const outcome = await refreshToken();
  if (outcome.kind === "ok") return outcome.accessToken;
  if (outcome.kind === "signed_out") {
    onLogout();
    throw new ApiError(401, { error: "session_expired" }, "session expired");
  }
  throw new ApiError(
    503,
    { error: outcome.message ?? "Can't reach the server right now — you're still signed in. Try again in a moment." },
    "server unavailable",
  );
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit & ApiOptions = {},
): Promise<T> {
  const { skipAuthRefresh, skipAuth, ...rest } = init;

  const headers = new Headers(rest.headers);
  if (rest.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!skipAuth) {
    const tok = getAccessToken();
    if (tok) headers.set("Authorization", `Bearer ${tok}`);
  }

  let res = await rawFetch(path, { ...rest, headers });

  if (res.status === 401 && !skipAuthRefresh && !skipAuth) {
    const newTok = await renewedToken();
    const retryHeaders = new Headers(headers);
    retryHeaders.set("Authorization", `Bearer ${newTok}`);
    res = await rawFetch(path, { ...rest, headers: retryHeaders });
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("Content-Type") ?? "";
  if (ct.includes("application/json")) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}
